import { Checkbox, DatePicker, Input, Select } from 'antd'
import getClients from 'api/clients/getClients'
import searchWorkReportContacts, {
	WorkReportContactOption,
} from 'api/workReports/searchWorkReportContacts'
import { WorkReportsFiltersState } from 'container/WorkReportsPageLayout'
import moment, { Moment } from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import handleFormError from 'utils/handleFormError'
import styles from './styles.module.scss'

const { RangePicker } = DatePicker
const { Search } = Input

type Props = {
	filters: WorkReportsFiltersState
	onChange: React.Dispatch<React.SetStateAction<WorkReportsFiltersState>>
}

const WorkReportsHeader: React.FC<Props> = ({ filters, onChange }) => {
	const [clientOptions, setClientOptions] = useState<
		Array<{ label: string; value: string }>
	>([])
	const [clientsLoading, setClientsLoading] = useState(false)
	const [contactOptions, setContactOptions] = useState<
		WorkReportContactOption[]
	>([])
	const [contactsLoading, setContactsLoading] = useState(false)

	const loadClients = async (query?: string) => {
		setClientsLoading(true)
		try {
			const response = await getClients({
				page: 1,
				size: 30,
				query: query || undefined,
				active: true,
			})
			setClientOptions(
				(response.records || []).map(client => ({
					value: String(client._id),
					label: String(client.organizationName || client.email || client._id),
				}))
			)
		} catch (error: any) {
			handleFormError(error)
		} finally {
			setClientsLoading(false)
		}
	}

	const loadContacts = async (query?: string) => {
		if (!filters.clientId) {
			setContactOptions([])
			return
		}
		setContactsLoading(true)
		try {
			const rows = await searchWorkReportContacts({
				clientId: filters.clientId,
				q: query || undefined,
				limit: 30,
			})
			setContactOptions(rows)
		} catch (error: any) {
			handleFormError(error)
		} finally {
			setContactsLoading(false)
		}
	}

	useEffect(() => {
		loadClients()
	}, [])

	useEffect(() => {
		loadContacts()
	}, [filters.clientId])

	const dateRange = useMemo((): [Moment, Moment] | null => {
		if (!filters.fromDayKey || !filters.toDayKey) return null
		return [
			moment(filters.fromDayKey, 'YYYY-MM-DD'),
			moment(filters.toDayKey, 'YYYY-MM-DD'),
		]
	}, [filters.fromDayKey, filters.toDayKey])

	return (
		<header className={styles.header}>
			<div className={styles.titleRow}>
				<h3 className={styles.title}>Work Reports</h3>
			</div>
			<div className={styles.filters}>
				<Select
					showSearch
					allowClear
					placeholder='Filter by client'
					className={styles.clientSelect}
					loading={clientsLoading}
					filterOption={false}
					value={filters.clientId}
					options={clientOptions}
					onSearch={value => {
						loadClients(value)
					}}
					onChange={value => {
						onChange(prev => ({
							...prev,
							clientId: value || undefined,
							contactId: undefined,
						}))
					}}
				/>
				<Select
					showSearch
					allowClear
					placeholder={
						filters.clientId ? 'Filter by contact' : 'Select a client first'
					}
					className={styles.contactSelect}
					disabled={!filters.clientId}
					loading={contactsLoading}
					filterOption={false}
					value={filters.contactId}
					options={contactOptions.map(row => ({
						value: row.contactId,
						label: row.mobile
							? `${row.contactName} (${row.mobile})`
							: row.contactName,
					}))}
					onSearch={value => {
						loadContacts(value)
					}}
					onChange={value => {
						onChange(prev => ({
							...prev,
							contactId: value || undefined,
						}))
					}}
				/>
				<Select
					allowClear
					placeholder='Review status'
					className={styles.statusSelect}
					value={filters.statuses || 'ALL'}
					options={[
						{ label: 'All statuses', value: 'ALL' },
						{ label: 'Open', value: 'open' },
						{ label: 'Accepted', value: 'accepted' },
						{ label: 'Rejected', value: 'rejected' },
						{ label: 'Skipped', value: 'skipped' },
					]}
					onChange={value => {
						onChange(prev => ({
							...prev,
							statuses: (value as WorkReportsFiltersState['statuses']) || 'ALL',
						}))
					}}
				/>
				<RangePicker
					className={styles.dateRange}
					value={dateRange}
					allowClear
					format='D MMM YYYY'
					onChange={values => {
						if (!values?.[0] || !values?.[1]) {
							onChange(prev => ({
								...prev,
								fromDayKey: undefined,
								toDayKey: undefined,
							}))
							return
						}
						onChange(prev => ({
							...prev,
							fromDayKey: values[0]!.format('YYYY-MM-DD'),
							toDayKey: values[1]!.format('YYYY-MM-DD'),
						}))
					}}
				/>
				<Search
					className={styles.search}
					allowClear
					enterButton
					placeholder='Search summary / feedback'
					onSearch={value => {
						onChange(prev => ({
							...prev,
							q: value.trim() || undefined,
						}))
					}}
				/>
				<Checkbox
					checked={!!filters.reviewedOnly}
					onChange={e => {
						onChange(prev => ({
							...prev,
							reviewedOnly: e.target.checked || undefined,
						}))
					}}
				>
					Reviewed only
				</Checkbox>
			</div>
		</header>
	)
}

export default WorkReportsHeader
